import React, { FC } from "react";
import { ButtonColor } from "enums/Button";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import {
  ConsolidationAddonsMethodsResponseDto,
  PackingMethodsResponseDto,
} from "types/api/cart";
import { OpenRightPanel } from "types/common/rightPanel";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import useProgressDelivery from "../../../../hooks/useProgressDelivery";
import injectReducer from "../../../../reduxFolder/injectReducer";
import outgoingReducer from "../../../../reduxFolder/reducers/outgoing";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  getConsolidationAddonsMethods,
  getPackingMethods,
} from "../../../../reduxFolder/reducers/services";
import { cartSelectCart } from "../../../../reduxFolder/selectors/cartSelectors";
import { authSelectFfid } from "../../../../reduxFolder/selectors/globalSelectors";
import { panelSelectOpened } from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  servicesSelectConsolidationAddons,
  servicesSelectPackingMethods,
} from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  Banner,
  BannerSubtitle,
  BannerTitle,
} from "../DemoShipments/DemoShipments.styles";
import {
  BackButton,
  OpenDetailsButton,
  StepBubble,
  StepButton,
  StepsList,
  StepStatic,
  StepText,
} from "./CurrentParcelGuide.styles";

interface GuideStep {
  key: string;
  label: string;
  onClick?: () => void;
  done?: boolean;
  disabled?: boolean;
}

interface CurrentParcelGuideProps {
  cart: any;
  packingMethods: any;
  consolidationAddons: any;
  userFfid: string;
  isPanelOpened: boolean;
  openRightPanel: OpenRightPanel;
  getPackingMethods: () => Promise<PackingMethodsResponseDto[]>;
  getConsolidationAddonsMethods: () => Promise<
    ConsolidationAddonsMethodsResponseDto[]
  >;
  onClose?: () => void;
}

const CurrentParcelGuide: FC<CurrentParcelGuideProps> = ({
  cart,
  packingMethods,
  consolidationAddons,
  userFfid,
  isPanelOpened,
  openRightPanel,
  getPackingMethods,
  getConsolidationAddonsMethods,
  onClose,
}) => {
  const { t } = useTranslation("common");
  const [, setAccess] = useProgressDelivery(userFfid);

  // PACKING_GRADE reads packingMethods from panelData and consolidation addons
  // from redux without fetching them itself; NotFinishedPanel normally loads
  // both, but the guide can open PACKING_GRADE before that panel ever mounts.
  useFetchOnceEffect(!packingMethods.size, getPackingMethods);
  useFetchOnceEffect(!consolidationAddons.size, getConsolidationAddonsMethods);

  const destinationAddress = cart.get("destination_address");
  const hasAddress = !!(
    (destinationAddress && destinationAddress.get("id")) ||
    cart.get("destination_address_id")
  );
  const hasDelivery = !!cart.get("preferred_carrier");
  const hasPacking = !!cart.getIn(["optional_line_item_codes", 0]);

  const basePanelData = Map({
    detailedItemID: cart.get("id"),
    parcel: cart,
    openedFromStorage: true,
  });

  const openParcelDetails = () => {
    openRightPanel("NOT_FINISHED", basePanelData);
  };

  // Keep in sync with DetailsPanel.clickChangingAddress
  const openAddressPanel = () => {
    openRightPanel(
      "CHANGING_ADDRESS",
      basePanelData
        .set("returnPreviousPanel", "NOT_FINISHED")
        .set("addressId", cart.get("destination_address_id"))
        .set("isCartVariant", true)
        .set("cart", cart)
        .set("setAccess", () => setAccess("delivery")),
    );
  };

  // Keep in sync with DetailsPanel.editDeliveryMethod
  const openDeliveryPanel = () => {
    openRightPanel(
      "DELIVERY_METHODS",
      basePanelData
        .set("detailedItemID", cart.get("id"))
        .set("returnPreviousPanel", "NOT_FINISHED")
        .set("deliveryCountry", cart.getIn(["destination_address", "country"]))
        .set("deliveryMethod", cart.get("preferred_carrier"))
        .set("isCartVariant", true)
        .set("cart", cart)
        .set("setAccess", () => setAccess("packing")),
    );
  };

  // Keep in sync with DetailsPanel.editPackingGrade
  const openPackingPanel = () => {
    openRightPanel(
      "PACKING_GRADE",
      basePanelData
        .set("returnPreviousPanel", "NOT_FINISHED")
        .set("consolidationSku", cart.get("sku"))
        .set("deliveryMethod", cart.get("preferred_carrier"))
        .set("packingCode", cart.getIn(["optional_line_item_codes", 0]))
        .set("packingMethods", packingMethods)
        .set("selectedConsolidationAddons", cart.get("addons_codes"))
        .set("isCartVariant", true)
        .set("cart", cart)
        .set("setAccess", (selectedAddonsIds: string[]) => {
          if (selectedAddonsIds && selectedAddonsIds.length) {
            setAccess("customInformation");
          } else {
            setAccess("addons");
          }
        }),
    );
  };

  const steps: GuideStep[] = [
    {
      key: "review",
      label: t("parcels.currentParcelGuide.steps.review"),
      onClick: openParcelDetails,
    },
    {
      key: "address",
      label: t("parcels.currentParcelGuide.steps.address"),
      onClick: openAddressPanel,
      done: hasAddress,
    },
    {
      key: "method",
      label: t("parcels.currentParcelGuide.steps.method"),
      onClick: openDeliveryPanel,
      done: hasDelivery,
      disabled: !hasAddress,
    },
    {
      key: "packing",
      label: t("parcels.currentParcelGuide.steps.packing"),
      onClick: openPackingPanel,
      done: hasPacking,
      disabled: !hasAddress || !hasDelivery,
    },
    {
      key: "customs",
      label: t("parcels.currentParcelGuide.steps.customs"),
      onClick: openParcelDetails,
    },
    {
      key: "checkout",
      label: t("parcels.currentParcelGuide.steps.checkout"),
    },
  ];

  return (
    <>
      <Banner>
        <BannerTitle>
          {t("parcels.currentParcelGuide.title", { sku: cart.get("sku") })}
        </BannerTitle>
        <BannerSubtitle>
          {t("parcels.currentParcelGuide.subtitle")}
        </BannerSubtitle>
        {!isPanelOpened && (
          <OpenDetailsButton
            color={ButtonColor.Primary}
            onClick={openParcelDetails}
          >
            {t("parcels.currentParcelGuide.openDetails")}
          </OpenDetailsButton>
        )}
      </Banner>
      <StepsList>
        {steps.map((step, index) => {
          const content = (
            <>
              <StepBubble $done={step.done}>
                {step.done ? "✓" : index + 1}
              </StepBubble>
              <StepText>{step.label}</StepText>
            </>
          );

          return (
            <li key={step.key}>
              {step.onClick ? (
                <StepButton
                  type="button"
                  disabled={step.disabled}
                  onClick={step.onClick}
                >
                  {content}
                </StepButton>
              ) : (
                <StepStatic>{content}</StepStatic>
              )}
            </li>
          );
        })}
      </StepsList>
      {onClose && (
        <BackButton color={ButtonColor.Primary} onClick={onClose}>
          {t("parcels.currentParcelGuide.backToList")}
        </BackButton>
      )}
    </>
  );
};

// DELIVERY_METHODS keeps its fetched list in the injected `outgoing` slice and
// in the classic flow relies on NOT_FINISHED having mounted (and injected it)
// first; the guide can open that panel directly, so ensure the slice exists.
const withOutgoingReducer = injectReducer({
  key: "outgoing",
  reducer: outgoingReducer,
});

const withConnect = connect(
  createStructuredSelector({
    cart: cartSelectCart,
    packingMethods: servicesSelectPackingMethods,
    consolidationAddons: servicesSelectConsolidationAddons,
    userFfid: authSelectFfid,
    isPanelOpened: panelSelectOpened,
  }),
  {
    openRightPanel,
    getPackingMethods,
    getConsolidationAddonsMethods,
  },
);

const enhance = compose(withOutgoingReducer, withConnect);

// injectReducer is untyped (@ts-nocheck) and swallows the own-props type;
// restate the public contract so call sites keep prop checking.
export default enhance(CurrentParcelGuide) as unknown as FC<{
  onClose?: () => void;
}>;
